import { Client, consoleApi, type ConsoleApi } from '@central/api';
import { queryOptions } from '@tanstack/react-query';
import { createContext, useContext } from 'react';

/** Builds the console API on /api; tests pass a fake fetch. */
export function createConsoleApi(fetchImpl?: typeof fetch): ConsoleApi {
  return consoleApi(new Client({ baseUrl: '/api', fetch: fetchImpl }));
}

export const ApiContext = createContext<ConsoleApi | null>(null);

/** The console API from the nearest ApiContext provider. */
export function useApi(): ConsoleApi {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi needs an ApiContext provider');
  return api;
}

/** Query keys. Mutations invalidate by these prefixes. */
export const keys = {
  me: ['auth', 'me'] as const,
  credentials: ['auth', 'credentials'] as const,
  trustedBrowsers: ['auth', 'trusted-browsers'] as const,
  notificationPrefs: ['me', 'notifications'] as const,
  settings: ['settings'] as const,
  staff: ['staff'] as const,
  staffSsoAll: ['staff-sso'] as const,
  /** Every staff SSO provider, whoever owns it. */
  staffSsoEvery: ['staff-sso', { every: true }] as const,
  /** Staff SSO providers of one owner: '' for Rethink's, else a reseller id. */
  staffSso: (resellerId: string) => ['staff-sso', resellerId] as const,
  overview: ['overview'] as const,
  customers: ['customers'] as const,
  customerList: (query: string, status: string, resellerId = '') =>
    ['customers', 'list', query, status, resellerId] as const,
  customer: (id: string) => ['customers', id] as const,
  customerUsers: (id: string) => ['customers', id, 'users'] as const,
  customerSso: (id: string) => ['customers', id, 'sso'] as const,
  customerCounts: (id: string) => ['customers', id, 'counts'] as const,
  licenses: ['licenses'] as const,
  licenseList: (customerId?: string) => ['licenses', 'list', customerId ?? ''] as const,
  licenseSearch: (q: object) => ['licenses', 'search', q] as const,
  license: (id: string) => ['licenses', id] as const,
  licenseChanges: (id: string) => ['licenses', id, 'changes'] as const,
  licenseActivations: (id: string) => ['licenses', id, 'activations'] as const,
  features: ['catalogue', 'features'] as const,
  products: ['catalogue', 'products'] as const,
  environments: ['environments'] as const,
  environmentLists: ['environments', 'list'] as const,
  /** One filtered, paged environment list; q is its EnvironmentListQuery without the cursor. */
  environmentList: (q: object = {}) => ['environments', 'list', q] as const,
  /** All of a customer's environments, every page; nested so environmentList invalidation reaches it. */
  environmentOptions: (customerId: string) => ['environments', 'list', customerId, 'all'] as const,
  environment: (id: string) => ['environments', id] as const,
  environmentUptime: (id: string) => ['environments', id, 'uptime'] as const,
  environmentMetrics: (id: string) => ['environments', id, 'metrics'] as const,
  environmentServices: (id: string) => ['environments', id, 'services'] as const,
  /** The report at or before `at`; nested so environmentServices invalidation reaches it. */
  environmentServicesAt: (id: string, at: string) => ['environments', id, 'services', at] as const,
  environmentMetricsTo: (id: string, end: string) => ['environments', id, 'metrics', end] as const,
  environmentVersions: (id: string) => ['environments', id, 'versions'] as const,
  environmentIncidents: (id: string) => ['environments', id, 'incidents'] as const,
  announcements: ['announcements'] as const,
  announcementList: ['announcements', 'list'] as const,
  announcement: (id: string) => ['announcements', id] as const,
  announcementReceipts: (id: string) => ['announcements', id, 'receipts'] as const,
  audiencePreview: (audience: unknown) => ['announcements', 'preview', audience] as const,
  releases: ['releases'] as const,
  resellers: ['resellers'] as const,
  reseller: (id: string) => ['resellers', id] as const,
  resellerSummary: (id: string) => ['resellers', id, 'summary'] as const,
  /** A reseller's users; under staff, so staff invalidation reaches it. */
  resellerStaff: (id: string) => ['staff', 'reseller', id] as const,
  partner: ['partner'] as const,
  partnerOverview: ['partner', 'overview'] as const,
  partnerCustomers: ['partner', 'customers'] as const,
  partnerCustomer: (id: string) => ['partner', 'customers', id] as const,
  partnerEnvironments: (customerId = '') =>
    ['partner', 'environments', 'list', customerId] as const,
  /** Holds an array of environments, never a page: keep it apart from partnerEnvironments. */
  partnerEnvironmentOptions: (customerId: string) =>
    ['partner', 'environments', 'options', customerId] as const,
  partnerEnvironment: (id: string) => ['partner', 'environments', id] as const,
  partnerEnvironmentUptime: (id: string) => ['partner', 'environments', id, 'uptime'] as const,
  partnerEnvironmentServices: (id: string) => ['partner', 'environments', id, 'services'] as const,
  partnerReleases: ['partner', 'releases'] as const,
  partnerTeam: ['partner', 'team'] as const,
  partnerProfile: ['partner', 'profile'] as const,
  customerOptions: ['customers', 'options'] as const,
  logs: (q: object) => ['logs', q] as const,
  logRate: (q: object) => ['logs', 'rate', q] as const,
  logViews: ['log-views'] as const,
  auditList: (q: object) => ['audit', 'list', q] as const,
  threads: ['threads'] as const,
  threadList: (q: object) => ['threads', 'list', q] as const,
  threadCount: (q: object) => ['threads', 'count', q] as const,
  /** Under threads, so a thread change refreshes the support report too. */
  ticketReport: (q: object) => ['threads', 'report', q] as const,
  partnerSupportReport: (q: object) => ['threads', 'partner-report', q] as const,
  thread: (id: string) => ['threads', id] as const,
  unreadCount: ['threads', 'unread-count'] as const,
  threadCrashReports: (id: string) => ['threads', id, 'crash-reports'] as const,
  threadTags: (q: string) => ['threads', 'tags', q] as const,
  threadWatchers: (id: string) => ['threads', id, 'watchers'] as const,
  crashReport: (id: string) => ['crash-reports', id] as const,
  cannedReplies: ['canned-replies'] as const,
  /** The replies for one thread kind; nested so cannedReplies invalidation reaches it. */
  cannedReplyList: (kind: string) => ['canned-replies', kind] as const,
};

export const meQuery = (api: ConsoleApi) =>
  queryOptions({
    queryKey: keys.me,
    queryFn: () => api.auth.me(),
    staleTime: 60_000,
    retry: false,
  });
